import * as React from "react";
import { useCallback, useEffect } from "react";
import { Dialog, styled } from "@mui/material";
import { useDiagnosticNavigation } from "../diagnosticAgent/useDiagnosticNavigation";
import { LoginWidget } from "user-management/src/components/loginWidget";
import { NewPasswordWidget } from "user-management/src/components/newPasswordWidget";
import { SignUpWidget } from "user-management/src/components/signUpWidget";
import { UserManagementPopupMode } from "../../context/diagnosticAgent";
import { BackButton } from "../diagnosticAgent/common/backButton";
import { Flipped, Flipper } from "react-flip-toolkit";
import { signOut } from "user-management/src/cognito/kahunUserAuth";
import {
  ACTION,
  CATEGORY,
  evt,
  resetAnalytics,
} from "fe-common/analytics/analytics";
import { usePatientCase } from "../diagnosticAgent/usePatientCase";
import { useRouteHelper } from "../diagnosticAgent/useRouteHelper";
import { useSearchQueriesContext } from "../../context/searchQuery";
import CloseIconButton from "fe-common/ui/icons/closeIconButton";
import { KahunColorMap } from "fe-common/ui/assets/colorMap";

//region [[ Styles ]]

const UserManagementPopupView = styled(Dialog)({
  overflow: "hidden",
  "& .MuiPaper-rounded": {
    borderRadius: "25px",
  },
  // "& .MuiContainer-root": {
  //   paddingTop: "8px",
  //   paddingBottom: "8px",
  // },
});

const BackToLogin = styled(BackButton)({
  position: "absolute",
  top: "20px",
  left: "20px",
});

const Content = styled("div")({
  overflow: "hidden",
});

const CloseButton = styled(CloseIconButton)({
  left: "auto",
  right: "20px",
  top: "20px",
  borderRadius: "50%",
  height: "35px",
  width: "35px",
  padding: 0,
  background: KahunColorMap.Primary,
  color: "white",
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  "& svg": {
    height: "12px",
    width: "12px",
    fill: KahunColorMap.White,
  },
  "&:hover svg": {
    fill: KahunColorMap.White,
  },
  "&:hover": {
    background: KahunColorMap.PrimaryDim,
  },
});
//endregion [[ Styles ]]

//region [[ Props ]]

export interface UserManagementPopupProps {
  className?: string;
}

//endregion [[ Props ]]

//region [[ Functions ]]
//endregion [[ Functions ]]

export const UserManagementPopup = ({ ...props }: UserManagementPopupProps) => {
  const {
    forceLogin,
    loginPopupState,
    loginPopupMessage,
    setLoginPopupState,
    setInWizard,
  } = useDiagnosticNavigation();
  const { handleReset } = usePatientCase();
  const { goToHome } = useRouteHelper();
  const { setAgreeOnTerms } = useSearchQueriesContext();

  useEffect(() => {
    if (loginPopupState !== UserManagementPopupMode.Closed) {
      evt({
        category: CATEGORY.USER_MANAGEMENT_POPUP,
        action: ACTION.USER_MANAGEMENT_POPUP_STATE_CHANGED,
        label: loginPopupState.toString(),
      });
    }
  }, [loginPopupState]);

  const closePopup = useCallback(
    (event?: object, reason?: string) => {
      if (!forceLogin || !reason || reason === "") {
        setLoginPopupState(UserManagementPopupMode.Closed);
      }
      //Since there are API calls that need to be called again with new roles
      window.location.reload();
    },
    [setLoginPopupState, forceLogin],
  );

  const handleSignUpClicked = useCallback(
    () => setLoginPopupState(UserManagementPopupMode.SignUp),
    [setLoginPopupState],
  );

  const handleForgotPasswordClicked = useCallback(
    () => setLoginPopupState(UserManagementPopupMode.ResetPassword),
    [setLoginPopupState],
  );

  const goToLogin = useCallback(
    () => setLoginPopupState(UserManagementPopupMode.Login),
    [setLoginPopupState],
  );

  useEffect(() => {
    if (loginPopupState === UserManagementPopupMode.Logout) {
      // handle log out
      signOut();
      resetAnalytics();
      handleReset();
      setInWizard(true);
      setAgreeOnTerms(false);
      setLoginPopupState(UserManagementPopupMode.Closed);
      // navigate to home
      goToHome();
    }
  }, [
    loginPopupState,
    handleReset,
    setAgreeOnTerms,
    setLoginPopupState,
    goToHome,
    setInWizard,
  ]);

  const open =
    loginPopupState !== UserManagementPopupMode.Closed &&
    loginPopupState !== UserManagementPopupMode.Logout;

  return (
    <UserManagementPopupView
      className={props.className}
      open={open}
      onClose={closePopup}
    >
      <Flipper
        flipKey={loginPopupState}
        spring={{ stiffness: 120, damping: 14 }}
        staggerConfig={{ default: { speed: 1 } }}
      >
        <Flipped key={`login-content`} flipId={`login-content`}>
          <Content>
            {!forceLogin && <CloseButton onClick={closePopup} />}
            {loginPopupState === UserManagementPopupMode.Login && (
              <LoginWidget
                onSignIn={(email) => closePopup()}
                onForgotPasswordClick={handleForgotPasswordClicked}
                onSignUpClick={handleSignUpClicked}
                message={loginPopupMessage}
              />
            )}
            {loginPopupState === UserManagementPopupMode.ResetPassword && (
              <>
                <BackToLogin onClick={goToLogin} />
                <NewPasswordWidget onResetComplete={goToLogin} />
              </>
            )}
            {loginPopupState === UserManagementPopupMode.SignUp && (
              <>
                <BackToLogin onClick={goToLogin} />
                <SignUpWidget onSignUpComplete={goToLogin} />
              </>
            )}
          </Content>
        </Flipped>
      </Flipper>
    </UserManagementPopupView>
  );
};
